/**
 * xr_ui_input — the VR UI's input and placement maths (XR P2 "VR UI / UX", 2026-09-28). PURE: plain {x,y,z} vectors
 * and {x,y,z,w} quaternions, no three, no DOM. Every pose here is in the AVATAR RIG's local frame (the frame three's
 * XR manager composes the head and the controllers in), so nothing depends on the FP64 floating origin.
 *
 * INPUT FROM ANY SOURCE (the research — sources in the hand-back):
 *  · Controllers: `tracked-pointer` + a gamepad; the trigger is the WebXR `select`.
 *  · Quest hands: `tracked-pointer`, an emulated pointing ray, and a pinch = `select`. Meta: "the pinch gesture itself
 *    causes a slight hand shift, which often pulls the ray reticle off small targets" — so a hand's ray is damped (a
 *    One Euro filter, Casiez 2012) and the target is FROZEN at pinch start. Quest reserves the PALM pinch (both hands;
 *    the left one leaves the session), so no UI here asks for a pinch with the palm toward the face.
 *  · Apple Vision Pro: `transient-pointer` — the source exists only for the pinch (inputsourceschange → selectstart →
 *    select → selectend → inputsourceschange), its ray starts at the eyes along the gaze and then follows the hand.
 *    With hand tracking granted, the two tracked hands take input slots 0 and 1 (and fire NO select), and the
 *    transient pointers arrive at 2 and 3 — the UI claims FOUR controller slots so three never drops them.
 *  · Meta hands UI: "avoid anchoring menus to the user's hand or wrist … spawning a menu from the wrist is fine, as long
 *    as the menu is static once it appears" — the wrist menu opens on the watch-check posture and then stays put.
 */

export interface V3 { x: number; y: number; z: number }
export interface Q4 { x: number; y: number; z: number; w: number }

export const v3 = (x: number, y: number, z: number): V3 => ({ x, y, z });
export const add = (a: V3, b: V3): V3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const sub = (a: V3, b: V3): V3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const scale = (a: V3, s: number): V3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const len = (a: V3): number => Math.hypot(a.x, a.y, a.z);
export function norm(a: V3): V3 { const l = len(a); return l > 1e-9 ? scale(a, 1 / l) : { x: 0, y: 0, z: -1 }; }

/** Rotate `v` by the unit quaternion `q`. */
export function rotate(v: V3, q: Q4): V3 {
  const ix = q.w * v.x + q.y * v.z - q.z * v.y;
  const iy = q.w * v.y + q.z * v.x - q.x * v.z;
  const iz = q.w * v.z + q.x * v.y - q.y * v.x;
  const iw = -q.x * v.x - q.y * v.y - q.z * v.z;
  return {
    x: ix * q.w + iw * -q.x + iy * -q.z - iz * -q.y,
    y: iy * q.w + iw * -q.y + iz * -q.x - ix * -q.z,
    z: iz * q.w + iw * -q.z + ix * -q.y - iy * -q.x,
  };
}

/** A quaternion about +Y by `yaw` radians. */
export function yawQuat(yaw: number): Q4 { return { x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) }; }

/** The quaternion that turns a surface whose front faces local +Z so that it faces from `pos` toward `eye` (yaw then
 *  pitch, no roll — the panel stays level: an angled panel, never a tilted one). */
export function faceToward(pos: V3, eye: V3): Q4 {
  const d = sub(eye, pos);
  const yaw = Math.atan2(d.x, d.z);
  const pitch = -Math.atan2(d.y, Math.hypot(d.x, d.z));
  const cy = Math.cos(yaw / 2), sy = Math.sin(yaw / 2), cp = Math.cos(pitch / 2), sp = Math.sin(pitch / 2);
  // q = qYaw * qPitch (intrinsic Y then X)
  return { x: cy * sp, y: sy * cp, z: -sy * sp, w: cy * cp };
}

/** The horizontal heading (radians, three's yaw: 0 = looking down −Z) of a forward vector. */
export function headingOf(fwd: V3): number { return Math.atan2(-fwd.x, -fwd.z); }

/** Unit forward (−Z) of a heading. */
export function forwardOf(heading: number): V3 { return { x: -Math.sin(heading), y: 0, z: -Math.cos(heading) }; }

/** Shortest signed angle a − b, in (−π, π]. Hostile inputs (±1e30) give 0, never a spin loop. */
export function angleDiff(a: number, b: number): number {
  const d = a - b;
  if (!Number.isFinite(d) || Math.abs(d) > 1e6) return 0;
  return Math.atan2(Math.sin(d), Math.cos(d));
}

// ─── Hit tests ──────────────────────────────────────────────────────────────────

/**
 * A ray against a rectangle of `wM × hM` centred at `pos`, facing its local +Z after `rot`. Hits from the FRONT only
 * (a panel is one-sided). Returns the distance along the ray and the uv (u → right, v → up) or null.
 */
export function rayHitRect(o: V3, d: V3, pos: V3, rot: Q4, wM: number, hM: number): { t: number; u: number; v: number; point: V3 } | null {
  const n = rotate({ x: 0, y: 0, z: 1 }, rot);
  const denom = dot(d, n);
  if (denom >= -1e-6) return null; // parallel, or hitting the back
  const t = dot(sub(pos, o), n) / denom;
  if (!(t > 0)) return null;
  const p = add(o, scale(d, t));
  const rel = sub(p, pos);
  const right = rotate({ x: 1, y: 0, z: 0 }, rot);
  const up = rotate({ x: 0, y: 1, z: 0 }, rot);
  const u = dot(rel, right) / wM + 0.5;
  const v = dot(rel, up) / hM + 0.5;
  if (u < 0 || u > 1 || v < 0 || v > 1) return null;
  return { t, u, v, point: p };
}

/** A ray against a sphere; the nearest positive distance or null. */
export function rayHitSphere(o: V3, d: V3, c: V3, r: number): number | null {
  const oc = sub(o, c);
  const b = dot(oc, d);
  const cc = dot(oc, oc) - r * r;
  const disc = b * b - cc;
  if (disc < 0) return null;
  const s = Math.sqrt(disc);
  const t0 = -b - s, t1 = -b + s;
  if (t0 > 0) return t0;
  if (t1 > 0) return t1;
  return null;
}

/** Signed distance of point `p` in front of a surface (along its +Z normal) and its uv, for the POKE test. */
export function pointOnRect(p: V3, pos: V3, rot: Q4, wM: number, hM: number): { d: number; u: number; v: number } {
  const rel = sub(p, pos);
  return {
    d: dot(rel, rotate({ x: 0, y: 0, z: 1 }, rot)),
    u: dot(rel, rotate({ x: 1, y: 0, z: 0 }, rot)) / wM + 0.5,
    v: dot(rel, rotate({ x: 0, y: 1, z: 0 }, rot)) / hM + 0.5,
  };
}

// ─── Pointer damping: the One Euro filter (Casiez, Roussel & Vogel, CHI 2012) ──

export class OneEuro {
  private x: number | null = null;
  private dx = 0;
  constructor(private readonly minCutoff = 1.2, private readonly beta = 0.4, private readonly dCutoff = 1.0) {}
  private static alpha(cutoff: number, dt: number): number { const tau = 1 / (2 * Math.PI * cutoff); return 1 / (1 + tau / dt); }
  filter(v: number, dt: number): number {
    if (this.x === null || !(dt > 0)) { this.x = v; this.dx = 0; return v; }
    const dv = (v - this.x) / dt;
    this.dx += OneEuro.alpha(this.dCutoff, dt) * (dv - this.dx);
    const cutoff = this.minCutoff + this.beta * Math.abs(this.dx);
    this.x += OneEuro.alpha(cutoff, dt) * (v - this.x);
    return this.x;
  }
  reset(): void { this.x = null; this.dx = 0; }
}

/** A damped ray: each component of the origin and the direction through its own One Euro filter. */
export class RayDamper {
  private readonly f = Array.from({ length: 6 }, (_, i) => (i < 3 ? new OneEuro(1.5, 0.6) : new OneEuro(1.0, 0.35)));
  step(o: V3, d: V3, dt: number): { o: V3; d: V3 } {
    const f = this.f;
    return {
      o: { x: f[0].filter(o.x, dt), y: f[1].filter(o.y, dt), z: f[2].filter(o.z, dt) },
      d: norm({ x: f[3].filter(d.x, dt), y: f[4].filter(d.y, dt), z: f[5].filter(d.z, dt) }),
    };
  }
  reset(): void { for (const x of this.f) x.reset(); }
}

// ─── Select arbitration ─────────────────────────────────────────────────────────

export type PointerKind = 'controller' | 'hand' | 'transient' | 'gaze' | 'screen';

/** The pointer kind from an XRInputSource's facts. */
export function pointerKindOf(targetRayMode: string | undefined, hasHand: boolean): PointerKind {
  if (targetRayMode === 'transient-pointer') return 'transient';
  if (targetRayMode === 'gaze') return 'gaze';
  if (targetRayMode === 'screen') return 'screen';
  return hasHand ? 'hand' : 'controller';
}
/** Rays that shake with the hand get damped; a controller's never does (it is already steady and must feel direct). */
export const dampedKind = (k: PointerKind): boolean => k === 'hand' || k === 'transient';

/**
 * What a completed select clicks. A controller clicks only what it is STILL on at release (a drag off cancels, the
 * desktop rule). A pinch — a hand or Vision Pro's transient pointer — clicks what it was on at PINCH START: the pinch
 * itself moves the hand, and the transient pointer's ray exists only for the gesture.
 */
export function resolveSelect(kind: PointerKind, pressId: string | null, releaseId: string | null): string | null {
  if (pressId === null) return null;
  if (kind === 'controller') return releaseId === pressId ? pressId : null;
  return pressId;
}

/**
 * The press target a `select` resolves against. The spec fires `selectstart` at the press and `select` + `selectend` at
 * the release; IWER (the emulated Quest) fires `select` THEN `selectstart` both at the PRESS. xr-ui (2026-09-28): a
 * `select` that arrives with no `selectstart` seen is taken as a whole click at this instant (what is under the ray now;
 * for a pinch, the damped hover first) — on a spec runtime `pressing` is always true there and nothing changes.
 */
export function pressAtSelect(kind: PointerKind, pressing: boolean, pressTarget: string | null, hover: string | null, fresh: string | null): string | null {
  if (pressing) return pressTarget;
  return kind === 'hand' ? (hover ?? fresh) : fresh;
}

/** POKE (direct touch — hands at the wrist menu): armed while the fingertip is `armM` in front, fires when it crosses
 *  `pressM` inside a widget, re-arms only after it backs out again. */
export interface PokeState { armed: boolean }
export const POKE = { armM: 0.02, pressM: 0.004, depthM: 0.03 } as const;
export function pokeStep(s: PokeState, d: number, onWidget: boolean): { state: PokeState; fire: boolean } {
  if (d > POKE.armM) return { state: { armed: true }, fire: false };
  if (s.armed && d < POKE.pressM && d > -POKE.depthM && onWidget) return { state: { armed: false }, fire: true };
  return { state: s, fire: false };
}

// ─── Placement ──────────────────────────────────────────────────────────────────

/** Where the world panel opens: `distance` ahead along the head's HORIZONTAL heading, `drop` under the eye, facing
 *  the eye (so it is angled back when below it). Opened once, then it stays — world-anchored, never head-locked. */
export function panelPose(head: V3, headFwd: V3, distance: number, drop: number): { pos: V3; rot: Q4 } {
  let f = { x: headFwd.x, y: 0, z: headFwd.z };
  if (len(f) < 1e-4) f = { x: 0, y: 0, z: -1 };
  f = norm(f);
  const pos = { x: head.x + f.x * distance, y: head.y - drop, z: head.z + f.z * distance };
  return { pos, rot: faceToward(pos, head) };
}

// ─── The wrist menu's watch-check posture ──────────────────────────────────────

/** WebXR gripSpace: −Z toward the thumb, +X out of the BACK of the RIGHT hand, −X out of the back of the LEFT. */
export function dorsalOf(hand: 'left' | 'right'): V3 { return { x: hand === 'left' ? -1 : 1, y: 0, z: 0 }; }
/** The wrist sits behind the fist, toward the forearm (+Z in grip space). */
export const WRIST_IN_GRIP: V3 = { x: 0, y: 0, z: 0.07 };

/** `along` (hands only): how far the fingers point AWAY from the eyes — a hand reaching out palm-down shows its back to
 *  the eyes too, and must not open the menu; a watch check holds the forearm ACROSS the body. */
export interface WristReading { anchor: V3; facing: number; gaze: number; dist: number; along?: number }
/** How much the back of the wrist faces the eyes, and how much the eyes look at it. */
export function readWrist(gripPos: V3, gripRot: Q4, hand: 'left' | 'right', head: V3, headFwd: V3): WristReading {
  const anchor = add(gripPos, rotate(WRIST_IN_GRIP, gripRot));
  const toHead = sub(head, anchor);
  const dist = len(toHead);
  const dorsal = rotate(dorsalOf(hand), gripRot);
  return { anchor, facing: dot(dorsal, norm(toHead)), gaze: dot(norm(headFwd), norm(scale(toHead, -1))), dist };
}

/** WebXR Hand Input: a joint's −Y points OUT OF THE PALM, −Z along the bone toward the fingertips — so the back of
 *  the hand is the wrist joint's +Y. xr-ui (2026-09-28): three updates only the joints (never the grip) for a source
 *  that carries a hand, so a tracked hand's watch check reads the 'wrist' joint instead of the grip. */
export const WRIST_JOINT_DORSAL: V3 = { x: 0, y: 1, z: 0 };
export function readWristJoint(jointPos: V3, jointRot: Q4, head: V3, headFwd: V3): WristReading {
  const anchor = jointPos;
  const toHead = sub(head, anchor);
  const dist = len(toHead);
  const dorsal = rotate(WRIST_JOINT_DORSAL, jointRot);
  const fingers = rotate({ x: 0, y: 0, z: -1 }, jointRot);
  const away = norm(scale(toHead, -1));
  return { anchor, facing: dot(dorsal, norm(toHead)), gaze: dot(norm(headFwd), away), dist, along: dot(fingers, away) };
}

export const WRIST_GATE = { openFacing: 0.55, openGaze: 0.6, maxDist: 0.75, dwellS: 0.25, rearmFacing: 0.3, maxAlong: 0.7 } as const;

/** The watch-check detector: the posture held for `dwellS` opens; after an open it must be RELEASED (the back of the
 *  wrist turned away) before it may open again, so a dismissed menu does not reopen under a still-raised arm. */
export class WristGate {
  private held = 0;
  private armed = true;
  /** Forget the posture (a new tracked source — controllers ↔ hands — is a new posture, not a still-raised arm). */
  reset(): void { this.held = 0; this.armed = true; }
  update(r: WristReading | null, dt: number): boolean {
    if (!r) { this.held = 0; this.armed = true; return false; }
    if (r.facing < WRIST_GATE.rearmFacing || r.dist > WRIST_GATE.maxDist) this.armed = true;
    const pose = r.facing >= WRIST_GATE.openFacing && r.gaze >= WRIST_GATE.openGaze && r.dist <= WRIST_GATE.maxDist && (r.along ?? 0) <= WRIST_GATE.maxAlong;
    this.held = pose ? this.held + Math.max(0, dt) : 0;
    if (this.armed && this.held >= WRIST_GATE.dwellS) { this.armed = false; this.held = 0; return true; }
    return false;
  }
  /** True while the posture is currently held (for "keep open"). */
  static holding(r: WristReading | null): boolean {
    return !!r && r.facing >= WRIST_GATE.rearmFacing && r.dist <= WRIST_GATE.maxDist;
  }
}

/** Where the wrist menu spawns: above the wrist and a little toward the eyes (the hand never covers it), facing the eyes. */
export function wristMenuPose(anchor: V3, head: V3): { pos: V3; rot: Q4 } {
  const toHead = norm(sub(head, anchor));
  const pos = add(add(anchor, { x: 0, y: 0.09, z: 0 }), scale(toHead, 0.05));
  return { pos, rot: faceToward(pos, head) };
}

// ─── The summon orb's lazy follow ───────────────────────────────────────────────

/** The orb keeps its bearing until the head turns more than `followDeg` away, then eases back in front — it is never
 *  locked to the head (a tag-along, low in view). */
export function orbStep(bearing: number, headHeading: number, dt: number, followDeg: number, rate: number): number {
  const off = angleDiff(headHeading, bearing);
  if (Math.abs(off) <= (followDeg * Math.PI) / 180) return bearing;
  const k = 1 - Math.exp(-rate * Math.max(0, dt));
  return bearing + off * k;
}
export function orbPos(head: V3, bearing: number, distance: number, drop: number): V3 {
  const f = forwardOf(bearing);
  return { x: head.x + f.x * distance, y: head.y - drop, z: head.z + f.z * distance };
}
